/**
 * portalApiClient — Axios instance dedicated to the portal configuration
 * service (portal-configuration-api.dev.camcom.ai).
 *
 * Responsibilities:
 *   1. Read the portal access_token from localStorage automatically.
 *   2. Attach Authorization: Bearer <token> to every request.
 *   3. Handle 401 responses by clearing the session and redirecting to /login.
 *
 * No component or service should manually set the Authorization header for
 * portal config API calls — this client handles it centrally.
 */

import axios from "axios";
import { PORTAL_CONFIG_API_URL } from "../config/urls";

// Read the portal access token from localStorage.
// Kept as a plain function (not imported from authService) to avoid
// circular-import issues since authService imports from this module indirectly.
const getPortalAccessToken = () => localStorage.getItem("portalAccessToken");

const portalApiClient = axios.create({
  baseURL: PORTAL_CONFIG_API_URL,
  headers: { "Content-Type": "application/json" },
});

// ── Request interceptor: inject portal Bearer token ──────────────────────────
portalApiClient.interceptors.request.use(
  (config) => {
    const token = getPortalAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(
      "[PortalAPI]",
      config.method?.toUpperCase(),
      config.baseURL + config.url,
    );
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor: handle portal token expiration ─────────────────────
// IMPORTANT: only clear portal-specific state on 401.
// Do NOT touch authToken / source_name / userProfile and do NOT redirect.
// The client-API interceptor (apiService.js) is responsible for full session
// logout.  A 401 here simply means the portal config is unavailable for now
// — it must never destroy an otherwise-valid login session.
portalApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn("[PortalAPI] 401 – portal token invalid/absent, clearing portal state only");
      localStorage.removeItem("portalAccessToken");
      localStorage.removeItem("portalConfig");
    }
    return Promise.reject(error);
  },
);

export default portalApiClient;
